import {
  Box,
  Button,
  Image,
  ListItem,
  Spacer,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { getAssetUrl } from '@cronos-app/chakra';
import { useCdcDepositCardService } from '@ui/views/card-cronos/core/utils';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import { CollapseWrapper } from '../shared/CollapseWrapper';
import { AddressInput } from './AddressInput';
import { ProgressBar } from './ProgressBar';
import type { StepItem } from './StepList';
import { StepItemContainer, StepList } from './StepList';
import { useToggleReminderStore } from './ToggleReminder';
import { useCardAction } from './useCardAction';

enum Step {
  Input = 1,
  Tips = 2,
}

export const BindView = () => {
  const [step, setStep] = useState(Step.Input);
  return (
    <>
      <ProgressBar
        total={2}
        done={step}
        mb="6"
        px={{
          base: 5,
          desktop: 6,
        }}
      />
      {step === Step.Input ? <InputView setStep={setStep} /> : <TipsView />}
    </>
  );
};

const InputView = ({ setStep }: { setStep: (step: Step) => void }) => {
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const t = useTranslations();
  const { bindCard } = useCardAction();
  const service = useCdcDepositCardService();
  const errorTip = useMemo(() => {
    if (!address) return '';
    if (service.validateDepositAddress(address)) return '';
    return t('card.Invalid address');
  }, [address, service, t]);
  return (
    <>
      <Stack
        px={{
          base: 5,
          desktop: 6,
        }}
        gap="4"
        mb="4"
      >
        <Text textStyle="subtitle1" textAlign="center" mb="32px">
          1. {t('card.Set up wallet')}
        </Text>
        <AddressInput
          address={address}
          onChange={setAddress}
          errorTip={errorTip}
        />
        <CollapseWrapper
          title={t('card.Find my wallet address on Crypto-com')}
          titleImage={
            <Image
              src={getAssetUrl('/images/cdc-bind-card/buy_and_sell_sg.png')}
              h="48px"
              pos="absolute"
              right="-3"
              alt="Buy and sell"
            />
          }
          boxProps={{
            border: '1px solid',
            borderColor: 'brand.gray-scale-border-10',
            px: 4,
            py: 3,
            bg: 'brand.background-transparent-blue',
          }}
        >
          <StepList steps={INPUT_STEPS} />
        </CollapseWrapper>
      </Stack>
      <Spacer />
      <Box
        borderTop="1px solid"
        borderColor="brand.gray-scale-border-10"
        px="5"
        py="4"
      >
        <Button
          variant="ghost"
          bgColor="#159DFF"
          rounded="4px"
          w="full"
          size="sm"
          onClick={() => {
            setLoading(true);
            bindCard(address)
              .then(() => {
                setStep(Step.Tips);
              })
              .catch(() => {})
              .finally(() => {
                setLoading(false);
              });
          }}
          isDisabled={!address || !!errorTip}
          isLoading={loading}
          textTransform="unset"
        >
          {t('card.Link')}
        </Button>
      </Box>
    </>
  );
};

const INPUT_STEPS: StepItem[] = [
  {
    title: (t) => t("card.Open the Crypto-com app and tap on 'Deposit'"),
    image: getAssetUrl('/images/cdc-bind-card/cdc-ss-1.png'),
  },
  {
    title: (t) => t('card.Choose ‘Crypto’ as the deposit type'),
    image: getAssetUrl('/images/cdc-bind-card/cdc-ss-2.png'),
  },
  {
    title: (t) => t('card.Select ‘USDC’ as the currency'),
    image: getAssetUrl('/images/cdc-bind-card/cdc-ss-3-light.png'),
  },
  {
    title: (t) => t('card.Pick the network ‘Cronos’'),
    image: getAssetUrl('/images/cdc-bind-card/cdc-ss-4-light.png'),
  },
  {
    title: (t) => t('card.Copy your ‘CRO Wallet Address’'),
    image: getAssetUrl('/images/cdc-bind-card/cdc-ss-5-light.png'),
  },
];

const TipsView = () => {
  const t = useTranslations();
  return (
    <>
      <Stack
        px={{
          base: 5,
          desktop: 6,
        }}
      >
        <Text textStyle="subtitle1" textAlign="center">
          2. {t('card.Enable auto top-up')}
        </Text>
        <Text textStyle="body2" textAlign="center">
          {t(
            'card.To link your card, open the Crypto-com app and enable auto top-up',
          )}
        </Text>
        <StepItemContainer
          title={t('card.Enable auto top-up')}
          image={getAssetUrl('/images/cdc-bind-card/cdc-enable-auto-1-new.png')}
          my="6"
        />
        <CollapseWrapper title={t('card.How do I toggle it on?')}>
          <StepList steps={AUTO_STEPS} mt="1" />
        </CollapseWrapper>
        <CollapseWrapper
          title={t('card.Why do I need to enable the auto top-up?')}
        >
          <UnorderedList textStyle="body2">
            <ListItem>{t('card.Why do I need to enable the auto p1')}</ListItem>
            <ListItem>{t('card.Why do I need to enable the auto p2')}</ListItem>
          </UnorderedList>
        </CollapseWrapper>
      </Stack>
      <Spacer minH="6" />
      <Box
        borderTop="1px solid"
        borderColor="brand.gray-scale-border-10"
        px="5"
        pb="4"
        pt="2"
      >
        <Button
          variant="action"
          w="full"
          color="#159DFF"
          size="sm"
          mb="2"
          onClick={useToggleReminderStore.getState().open}
          textTransform="unset"
        >
          {t('card.Do it later')}
        </Button>
        {/* <Link href={URL_SCHEME.cdcAppAutoTopUp} isExternal>
          <Button
            variant="ghost"
            bgColor="#159DFF"
            w="full"
            size="sm"
            rounded="4px"
            textTransform="unset"
          >
            {t('card.Open the Crypto-com app')}
          </Button>
        </Link> */}
      </Box>
    </>
  );
};

const AUTO_STEPS: StepItem[] = [
  {
    title: (t) => t('card.Open the Crypto-com app and tap on ‘Card’'),
    image: getAssetUrl('/images/cdc-bind-card/cdc-card-entry-1.png'),
  },
  {
    title: (t) => t('card.Toggle on ‘Auto Top-Up With Cronos Chain’'),
    image: getAssetUrl('/images/cdc-bind-card/auto-off-setting-new.png'),
  },
  {
    title: (t) => t('card.Click ‘Confirm’'),
    image: getAssetUrl('/images/cdc-bind-card/auto-on-confirm-new-1.png'),
  },
  {
    title: (t) => t('card.Enter passcode'),
    image: getAssetUrl('/images/cdc-bind-card/auto-on-pwd-new.png'),
  },
  {
    title: (t) => t('card.Auto top-up enabled'),
    image: getAssetUrl('/images/cdc-bind-card/auto-on-setting-new.png'),
  },
];
